import React, { useEffect, useState } from 'react';
import { X, Loader2, Clock, Mail, Phone, Tag, CalendarClock, FileText, Target, TrendingUp } from 'lucide-react';
import { Lead, leadsApi } from '../services/api';
import toast from 'react-hot-toast';
import { STAGE_PROBABILITY } from './LeadCard';

interface LeadStats {
  proposalCount: number;
  proposals: Array<{
    _id: string;
    subject: string;
    status: string;
    sentAt?: string;
    createdAt?: string;
    openedAt?: string | null;
    openCount: number;
    total: number;
  }>;
}

interface LeadDetailsModalProps {
  lead: Lead | null;
  onClose: () => void;
  onEdit: (lead: Lead) => void;
}

const STAGE_LABELS: Record<string, string> = {
  new: 'Prospecção',
  prospecting: 'Prospecção Avançada',
  contacted: 'Contactados',
  negotiation: 'Em Negociação',
  proposal: 'Proposta Enviada',
  pending: 'Aguardando',
  won: 'Fechado Ganho',
  lost: 'Fechado Perdido',
};

const SOURCE_LABELS: Record<string, string> = {
  website: 'Website',
  referral: 'Indicação / Referência',
  social: 'Redes Sociais',
  cold_call: 'Chamada a frio',
  walk_in: 'Passou pelo balcão',
  other: 'Outro',
};

const STATUS_BADGE: Record<string, string> = {
  sent: 'bg-blue-100 text-blue-700',
  opened: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-gray-200 text-gray-600',
  draft: 'bg-gray-100 text-gray-500',
};

const fmtMT = (v: number) =>
  new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' })
    .format(v || 0)
    .replace('MZN', 'MT');

const fmtDuration = (ms: number) => {
  if (ms <= 0) return 'hoje';
  const days = Math.floor(ms / (1000 * 3600 * 24));
  if (days > 0) return `${days} dia${days > 1 ? 's' : ''}`;
  const hours = Math.floor(ms / (1000 * 3600));
  if (hours > 0) return `${hours} hora${hours > 1 ? 's' : ''}`;
  const mins = Math.floor(ms / (1000 * 60));
  return `${mins} min`;
};

export const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ lead, onClose, onEdit }) => {
  const [stats, setStats] = useState<LeadStats>({ proposalCount: 0, proposals: [] });
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (!lead?._id) return;
    setLoadingStats(true);
    leadsApi
      .getStats(lead._id)
      .then((res) => setStats({ proposalCount: res.proposalCount, proposals: res.proposals }))
      .catch((err) => {
        console.error('Erro ao carregar stats do lead:', err);
        toast.error('Não foi possível carregar as propostas do lead');
      })
      .finally(() => setLoadingStats(false));
  }, [lead?._id]);

  if (!lead) return null;

  const registeredFor = lead.createdAt
    ? fmtDuration(Date.now() - new Date(lead.createdAt).getTime())
    : '—';

  const expectedDate = lead.expectedCloseDate
    ? new Date(lead.expectedCloseDate).toLocaleDateString('pt-PT')
    : null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[115] p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 sm:px-6 py-4 text-white flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold truncate">{lead.name}</h2>
            <p className="text-sm text-white/80 truncate">
              {STAGE_LABELS[lead.stage] || lead.stage}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 flex flex-col gap-5 overflow-y-auto flex-1">
          {/* Resumo rápido */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-indigo-600 uppercase tracking-wide">
                <Clock size={11} /> Registado
              </div>
              <p className="text-sm font-bold text-indigo-900 mt-0.5">há {registeredFor}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">
                <FileText size={11} /> Propostas
              </div>
              <p className="text-sm font-bold text-emerald-900 mt-0.5">
                {loadingStats ? '…' : `${stats.proposalCount} enviada${stats.proposalCount === 1 ? '' : 's'}`}
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 uppercase tracking-wide">
                <Target size={11} /> Valor
              </div>
              <p className="text-sm font-bold text-amber-900 mt-0.5 truncate">
                {Number(lead.dealValue) > 0 ? fmtMT(Number(lead.dealValue)) : '—'}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 uppercase tracking-wide">
                <TrendingUp size={11} /> Conversão
              </div>
              <p className="text-sm font-bold text-blue-900 mt-0.5">
                {STAGE_PROBABILITY[lead.stage] ?? 0}%
              </p>
            </div>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Contacto
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <Mail size={15} className="text-gray-400" />
                <span className="truncate">{lead.email || '—'}</span>
              </div>
              {lead.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={15} className="text-gray-400" />
                  <span>{lead.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Dados do negócio */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Dados do negócio
            </h3>
            {(() => {
              const originFromNotes = lead.notes
                ?.split('\n')
                .find((line) => line.toLowerCase().startsWith('origem:'))
                ?.replace(/^origem:\s*/i, '');
              const displaySource = originFromNotes || SOURCE_LABELS[lead.source || ''] || lead.source || '—';

              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Tag size={14} className="text-gray-400" />
                    <span className="text-gray-500">Origem:</span>
                    <span className="font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">{displaySource}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CalendarClock size={14} className="text-gray-400" />
                    <span className="text-gray-500">Fecho previsto:</span>
                    <span className="font-medium">{expectedDate || '—'}</span>
                  </div>
                </div>
              );
            })()}
            {lead.lostReason && (
              <div className="mt-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-sm text-red-700">
                <strong>Motivo da perda:</strong> {lead.lostReason}
              </div>
            )}
            {lead.notes && (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600">
                {lead.notes}
              </div>
            )}
          </div>

          {/* Histórico de propostas */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Propostas enviadas ({loadingStats ? '…' : stats.proposalCount})
            </h3>
            {loadingStats ? (
              <div className="flex items-center justify-center gap-2 py-6 text-gray-400">
                <Loader2 size={18} className="animate-spin" /> A carregar...
              </div>
            ) : stats.proposals.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                Nenhuma proposta enviada a este lead ainda.
              </p>
            ) : (
              <div className="space-y-2">
                {stats.proposals.map((p) => (
                  <div
                    key={p._id}
                    className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{p.subject}</p>
                      <p className="text-[11px] text-gray-400">
                        {p.sentAt
                          ? new Date(p.sentAt).toLocaleDateString('pt-PT')
                          : p.createdAt
                            ? new Date(p.createdAt).toLocaleDateString('pt-PT')
                            : '—'}{' '}
                        · {fmtMT(p.total)}
                        {p.openCount > 0 && ` · ${p.openCount} aberta${p.openCount === 1 ? '' : 's'}`}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${
                        STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              onClick={onClose}
              className="w-full sm:w-1/2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
            >
              Fechar
            </button>
            <button
              onClick={() => {
                onEdit(lead);
                onClose();
              }}
              className="w-full sm:w-1/2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition shadow-sm"
            >
              Editar lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
